import { useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "../components/ui/dialog";
import { ZoomIn, X } from "lucide-react";
import usePageMeta from "../hooks/usePageMeta";

// Editorial mood board - easy to extend. Each entry: src, alt, caption, span (Tailwind classes), aspect
const PIECES = [
  {
    id: "pendulum-lace",
    src: "https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/9y6uucx7_Cleo%20erotic%20hypnosis%20pocket%20watch.jpg",
    alt: "Gold pocket watch suspended over black lace",
    caption: "The Pendulum",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  {
    id: "hypno-glasses-portrait",
    src: "https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/ibi954ka_london%20uk%20erotic%20hypnotist.jpg",
    alt: "Queen Cleopatra in hypno-spiral glasses",
    caption: "Glasses · London",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  {
    id: "champagne-watch",
    src: "https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/ct5t5vid_erotic%20hypnosis%20UK%20pocket%20watch.jpg",
    alt: "Champagne, rose and gold pocket watch",
    caption: "Champagne Hour",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  {
    id: "dolls-spiral",
    src: "https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/jd8gr8d7_Dolls.jpg",
    alt: "Three dolls arranged before a hypnotic spiral",
    caption: "The Spiral - Emblem of the Practice",
    span: "col-span-4 row-span-2",
    aspect: "aspect-[2/1]",
  },
  {
    id: "crystal-pendant",
    src: "https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/ipgrh8hm_UK%20Erotic%20Hypnotist.jpg",
    alt: "Crystal pendant held against skin in low light",
    caption: "Crystal",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  {
    id: "pocket-watch-playful",
    src: "https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/9kie8vt6_Cleopatra%20uk%20erotic%20hypnosis.jpg",
    alt: "Queen Cleopatra with hypno-spiral glasses and gold pocket watch",
    caption: "A Study in Surrender",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  {
    id: "hands-chain",
    src: "https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/qmzz8za3_Cleo%20erotic%20hypnosis%20pocket%20watch%202.jpg",
    alt: "Hands holding a gold pocket watch chain",
    caption: "Hands · Chain · Gesture",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  {
    id: "banner-gif",
    src: "https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/br3gs06d_cleopatra_banner_1.gif",
    alt: "Animated banner - do you really want to spend the rest of your life wandering",
    caption: "Banner · Animation",
    span: "col-span-4 row-span-2",
    aspect: "aspect-[2/1]",
  },
  {
    id: "scott-induction",
    src: "https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/i4rhd52p_Scotts%20Initial%20Induction%20Preview.gif",
    alt: "Animated preview of Scott's initial induction",
    caption: "Scott · Initial Induction",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  {
    id: "crystal-time",
    src: "https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/hmni9a4o_crystal%20time%20with%20cleo%20preview.gif",
    alt: "Crystal Time with Cleo - animated preview",
    caption: "Crystal Time",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  {
    id: "scott-tickled",
    src: "https://customer-assets.emergentagent.com/job_trance-bookings/artifacts/y38xseru_Scott%20tickled%20preview.gif",
    alt: "Scott Tickled - playful animated preview",
    caption: "Laughter as Trance",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  // - New editorial photography (Aug 2026)
  {
    id: "champagne-moet",
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/7mmnljv6_IMG_7305.jpg",
    alt: "Gold pocket watch lowered between two champagne flutes beside a Moët bottle and single rose",
    caption: "Moët & the Pendulum",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  {
    id: "boots-pendulum",
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/l4bmmwkt_IMG_7481.jpg",
    alt: "White thigh-high boots with a gold pocket watch pendulum against red",
    caption: "White Boots · Red Silk",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  {
    id: "metronome",
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/3u4j2hg4_IMG_7602.jpg",
    alt: "A hand tipping the arm of a wooden metronome",
    caption: "Tempo · The Metronome",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  {
    id: "hands-knee",
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/eu94hcyd_IMG_7542.jpg",
    alt: "Clasped hands resting on a knee, warm skin, deep shadow",
    caption: "Hands · Held",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  // - Feet series
  {
    id: "foot-pendulum-sole",
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/z32pfpgc_IMG_7549.jpg",
    alt: "Sole of a foot with a gold pocket watch pendulum on black",
    caption: "Sole · The Pendulum",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  {
    id: "feet-pearls",
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/trteddsh_IMG_7532.jpg",
    alt: "Pearls threaded through the toes on black velvet",
    caption: "Pearls · Threaded",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  {
    id: "feet-organza",
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/ww8u1jds_IMG_7518.jpg",
    alt: "Feet veiled in sheer organza on a warm gold ground",
    caption: "Organza · Gold",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  {
    id: "feet-white-lace",
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/101id4pi_IMG_7526.jpg",
    alt: "Bare foot with red pedicure emerging from white lace",
    caption: "Lace · Red Polish",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  {
    id: "feet-cherries",
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/5y1bkwhn_IMG_7572.jpg",
    alt: "Two cherries held between painted toes",
    caption: "Cherries",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  {
    id: "feet-gold-heels",
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/knz0susa_IMG_7556.jpg",
    alt: "Gold peep-toe heels with bows, red pedicure",
    caption: "Gold Heels",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
  {
    id: "feet-sole-heel",
    src: "https://customer-assets-lxgj4vgw.emergentagent.net/job_trance-bookings/artifacts/zl806x6z_IMG_7564.jpg",
    alt: "A sole in soft focus with a gold heel dangling behind",
    caption: "Just Kicked Off",
    span: "col-span-2 row-span-2",
    aspect: "aspect-square",
  },
];

export default function Gallery() {
  usePageMeta({title:"Gallery",description:"A curated visual portfolio of Queen Cleopatra's London erotic hypnosis practice - pocket watches, crystals, portraits and props.",path:"/gallery"});
  const [active, setActive] = useState(null);

  return (
    <div data-testid="page-gallery">
      {/* Editorial header */}
      <section className="py-24 md:py-32 max-w-7xl mx-auto px-6 md:px-10 lg:px-16">
        <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-end">
          <div className="md:col-span-7">
            <div className="eyebrow mb-5">A Visual Diary</div>
            <h1 className="font-serif text-5xl md:text-7xl text-cream leading-[0.95]">
              The Salon, <em className="text-gold">in pictures</em>.
            </h1>
          </div>
          <div className="md:col-span-5 text-cream/70 leading-relaxed text-sm md:text-base">
            A slow, private mood board. Pendulums, candlelight, hands and chains -
            the iconography of the practice, collected.
            <span className="block mt-3 text-xs uppercase tracking-[0.22em] text-cream/45">Click any image to enlarge · Adults 18+</span>
          </div>
        </div>
      </section>

      {/* Bento mood board grid */}
      <section className="max-w-7xl mx-auto px-6 md:px-10 lg:px-16 pb-24">
        <div className="grid grid-cols-2 md:grid-cols-8 gap-3 md:gap-4 auto-rows-[clamp(110px,16vw,180px)]" data-testid="gallery-grid">
          {PIECES.map((p) => (
            <button
              key={p.id}
              onClick={() => setActive(p)}
              data-testid={`gallery-tile-${p.id}`}
              className={`group relative overflow-hidden border border-[#c9a04b]/20 bg-charcoalMuted ${p.span} ${p.aspect ? "" : ""} transition-all hover:border-[#c9a04b]/70 hover:z-10`}
              aria-label={`Enlarge: ${p.caption}`}
            >
              <img
                src={p.src}
                alt={p.alt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
              />
              <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                style={{
                  background:
                    "linear-gradient(180deg, rgba(17,19,25,0) 50%, rgba(17,19,25,0.85) 100%)",
                }}
              />
              {/* Caption - visible on hover */}
              <div className="absolute bottom-3 left-3 right-3 flex items-end justify-between opacity-0 translate-y-2 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-500">
                <span className="text-[10px] uppercase tracking-[0.22em] text-gold drop-shadow-[0_1px_2px_rgba(0,0,0,0.6)]">{p.caption}</span>
                <ZoomIn size={14} className="text-cream/80" strokeWidth={1.5} />
              </div>
              {/* Subtle inset frame */}
              <div className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-[#c9a04b]/0 group-hover:ring-[#c9a04b]/40 transition-all" />
            </button>
          ))}
        </div>

        <p className="mt-10 max-w-2xl text-xs text-cream/40 leading-relaxed tracking-wider">
          New entries are added as the archive grows. All imagery is the property of Queen Cleopatra
          and shared at her discretion. Photographs are tasteful and consensual. Adults 18+.
        </p>
      </section>

      {/* Lightbox */}
      <Dialog open={!!active} onOpenChange={(o) => !o && setActive(null)}>
        <DialogContent
          className="max-w-5xl w-[95vw] p-0 border-[#c9a04b]/40 bg-charcoal rounded-none [&>button]:hidden"
          data-testid="gallery-lightbox"
        >
          {active && (
            <>
              <DialogTitle className="sr-only">{active.caption}</DialogTitle>
              <button
                onClick={() => setActive(null)}
                aria-label="Close"
                data-testid="gallery-lightbox-close"
                className="absolute top-4 right-4 z-20 w-10 h-10 inline-flex items-center justify-center border border-[#c9a04b]/40 bg-charcoal/80 backdrop-blur text-cream hover:text-gold hover:border-gold transition-colors"
              >
                <X size={16} />
              </button>
              <div className="relative bg-charcoal">
                <img
                  src={active.src}
                  alt={active.alt}
                  className="w-full max-h-[80vh] object-contain"
                />
                <figcaption className="absolute left-0 right-0 bottom-0 p-5 md:p-7 flex items-end justify-between bg-gradient-to-t from-charcoal/95 via-charcoal/60 to-transparent">
                  <div>
                    <div className="eyebrow mb-1">{active.caption}</div>
                    <div className="text-cream/70 text-xs md:text-sm max-w-md">{active.alt}</div>
                  </div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-cream/45 hidden md:block">Queen Cleopatra · MMXXVI</div>
                </figcaption>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
